import {useEffect, useState} from "react";
import apiService from "src/services/ApiCallService";
import ErrorUtils from "src/utils/ErrorUtils";
import StringUtils from "src/utils/stringUtils";
import JEWorksheetList, {JEWorksheet, JEWorksheetTemplate} from "src/models/je-allocation/JEWorksheetList";
import JEAllocationUtils from "src/services/je-allocation/JEAllocationUtils";
import CONSTANTS from "src/utils/constants";
import CreateWorksheetRequest from "src/models/tp-allocation/CreateWorksheetRequest";
import {Worksheet, WorksheetTemplate} from "src/models/tp-allocation/WorksheetList";
import CreateJEWorksheetRequest from "src/models/je-allocation/CreateJEWorksheetRequest";
import DateUtils from "src/utils/dateUtils";

export default class JEAllocationService {

    getJEWorksheetsAndTemplates(query: string): [JEWorksheetList, boolean, string] {
        const [result, setResult] = useState(null as unknown as JEWorksheetList);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function getData() {
                try {
                    setLoading(true);
                    const response = await apiService.getJEAllocationTemplatesAndWorksheets();
                    const jeWorksheetList: JEWorksheetList = await response.json();
                    jeWorksheetList.templates?.forEach(x => x.placeholdersMap = JEAllocationUtils.convertToPlaceholdersMap(x.placeholders || []));
                    jeWorksheetList.worksheets?.forEach((x: any) => x.lastExecutionTimeDisplay = DateUtils.formatTimestamp(x.lastExecutionTimestamp));
                    setResult(jeWorksheetList);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!StringUtils.isNullOrEmpty(query)) {
                getData();
            }
        }, [query]);
        return [result, loading, error]
    }

    createJEWorksheet(payload: CreateJEWorksheetRequest): [JEWorksheet, boolean, string] {
        const [result, setResult] = useState(null as unknown as Worksheet);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData(payload: CreateWorksheetRequest) {
                try {
                    setLoading(true);
                    const response = await apiService.createJEAllocationWorksheet(payload);
                    const json = await response.json();
                    setResult(json.worksheet);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                postData(payload);
            }
        }, [payload]);
        return [result, loading, error]
    }

    createJEWorksheetTemplate(query: string, templateName: string): [JEWorksheetTemplate, boolean, string] {
        const [result, setResult] = useState(null as unknown as WorksheetTemplate);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData(templateName: string) {
                try {
                    setLoading(true);
                    const response = await apiService.createJEAllocationWorksheetTemplate(templateName);
                    const json = await response.json();
                    setResult(json.template);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(templateName)) {
                postData(templateName);
            }
        }, [query]);
        return [result, loading, error]
    }

    getAllCoaSegments(query: string) : [Map<string, string[]> | null, boolean, string] {
        // Using a local map so that callers don't get notified when the map is partially populated
        const localMap = new Map<string, string[]>();
        const [lovMap, setLOVMap] = useState(null as Map<string, string[]> | null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState('');

        // Populates the map entries as responses arrive
        async function handleCoaResponse(value: Response, segmentKey: string){
            localMap.set(segmentKey, (await value.json()).coaData);
        }

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const [channels, companies, costCenters, accounts, locations, products, projects] = await Promise.all([
                        await apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.WEB_API),
                        apiService.getCoaData(CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.WEB_API),
                    ])
                    await handleCoaResponse(channels, CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI);
                    await handleCoaResponse(companies, CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI);
                    await handleCoaResponse(costCenters, CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI);
                    await handleCoaResponse(accounts, CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI);
                    await handleCoaResponse(locations, CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI);
                    await handleCoaResponse(products, CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI);
                    await handleCoaResponse(projects, CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI);
                    // Setting the return map here so that callers get notified
                    setLOVMap(localMap);
                }
                catch (ex) {
                    console.error(ex);
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
        }, [query]);
        return [lovMap, loading, error]
    }
}
